import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, AlertTriangle, Users, TrendingDown, Building2, Globe, Truck, DollarSign, Info } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article8 = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-white">
      <div className="container mx-auto px-6 py-8">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Badge className="bg-red-600 text-white mr-4">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Market Consolidation
            </Badge>
            <span className="text-sm text-gray-600">Trans.info / Tübingen Local Court</span>
          </div>
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            April 2026
            <span className="mx-2">•</span>
            <span>6 min read</span>
          </div>
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.8.title')}
          </h1>

          <img
            src="https://cdn-trans.info/uploads/2026/04/f4cb8e4bb66cba9eb4ca784dc09-en.webp"
            alt="Betz International insolvency"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.8.intro')}
            </p>

            {/* Key Stats */}
            <div className="bg-red-50 p-6 rounded-lg my-8 border border-red-200">
              <h3 className="font-bold text-red-900 mb-4">
                {language === 'ko' ? '핵심 수치' : 'Key Figures'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="text-center bg-white rounded p-3">
                  <Users className="h-5 w-5 mx-auto mb-1 text-red-600" />
                  <p className="text-xs text-gray-500 mb-1">{language === 'ko' ? '영향받는 직원' : 'Employees at Risk'}</p>
                  <p className="text-3xl font-bold text-red-600">140</p>
                  <p className="text-xs text-gray-500">{language === 'ko' ? '임금 3개월 보장' : 'Wages secured 3 months'}</p>
                </div>
                <div className="text-center bg-white rounded p-3">
                  <Building2 className="h-5 w-5 mx-auto mb-1 text-orange-600" />
                  <p className="text-xs text-gray-500 mb-1">{language === 'ko' ? '창립' : 'Founded'}</p>
                  <p className="text-3xl font-bold text-orange-600">1945</p>
                  <p className="text-xs text-gray-500">{language === 'ko' ? '81년 역사' : '81-year history'}</p>
                </div>
                <div className="text-center bg-white rounded p-3">
                  <AlertTriangle className="h-5 w-5 mx-auto mb-1 text-yellow-600" />
                  <p className="text-xs text-gray-500 mb-1">{language === 'ko' ? 'Nothegger (AT)' : 'Nothegger (AT)'}</p>
                  <p className="text-xl font-bold text-yellow-600">300 {language === 'ko' ? '명' : 'staff'}</p>
                  <p className="text-xs text-gray-500">{language === 'ko' ? '부채 €1,500만+' : 'Liabilities €15M+'}</p>
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.8.section1.title')}
            </h2>
            <p className="mb-6">{t('article.8.p1')}</p>
            <p className="mb-6">{t('article.8.p2')}</p>

            {/* Company Profile */}
            <div className="bg-gray-50 p-6 rounded-lg my-8 border">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <Info className="mr-2 h-5 w-5 text-gray-600" />
                {language === 'ko' ? '회사 프로필: Betz International / Willi Betz 그룹' : 'Company Profile: Betz International / Willi Betz Group'}
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                {[
                  { label: language === 'ko' ? '창립' : 'Founded', value: '1945' },
                  { label: language === 'ko' ? '본사' : 'Headquarters', value: language === 'ko' ? '조넨뷜, 바덴뷔르템베르크, 독일' : 'Sonnenbühl, Baden-Württemberg, Germany' },
                  { label: language === 'ko' ? '그룹' : 'Group', value: 'Willi Betz Group' },
                  { label: language === 'ko' ? '직원' : 'Employees', value: language === 'ko' ? '약 140명 (파산 영향)' : '~140 (affected by insolvency)' },
                  { label: language === 'ko' ? '파산 법원' : 'Insolvency Court', value: language === 'ko' ? '튀빙겐 지방 법원 (2026년 4월 7일)' : 'Tübingen Local Court (7 April 2026)' },
                  { label: language === 'ko' ? '관재인' : 'Administrator', value: 'Dr. Dirk Poff (Provisional)' },
                ].map((item, i) => (
                  <div key={i} className="flex justify-between border-b pb-2">
                    <span className="font-medium text-gray-600">{item.label}</span>
                    <span className="text-gray-800">{item.value}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                {language === 'ko'
                  ? '* 파산은 Betz International에만 해당됩니다. Willi Betz 그룹의 다른 계열사는 영향을 받지 않습니다.'
                  : '* The insolvency affects only Betz International; the other companies of the Willi Betz Group are not affected.'}
              </p>
            </div>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.8.section2.title')}
            </h2>
            <p className="mb-6">{t('article.8.p3')}</p>
            <p className="mb-6">{t('article.8.p4')}</p>

            {/* Root Causes */}
            <div className="bg-orange-50 p-6 rounded-lg my-8 border border-orange-200">
              <h3 className="font-bold text-orange-900 mb-4 flex items-center">
                <TrendingDown className="mr-2 h-5 w-5" />
                {language === 'ko' ? '파산 주요 원인 — 업계 구조적 압박' : 'Key Insolvency Drivers — Structural Industry Pressures'}
              </h3>
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                {[
                  {
                    icon: <DollarSign className="h-4 w-4 text-red-600" />,
                    title: language === 'ko' ? '에너지·경유비 급등' : 'Soaring Energy & Diesel Costs',
                    desc: language === 'ko' ? '이란 전쟁 이후 EU 경유 +20%, 마진 압박 심화' : 'EU diesel +20% post-Iran war, squeezing already-thin margins',
                  },
                  {
                    icon: <TrendingDown className="h-4 w-4 text-orange-600" />,
                    title: language === 'ko' ? '유럽 경쟁 속 가격 압박' : 'European Competition Price Pressure',
                    desc: language === 'ko' ? '동유럽 저비용 운송업체와의 지속적인 가격 경쟁' : 'Ongoing undercutting by lower-cost Eastern European operators',
                  },
                  {
                    icon: <Truck className="h-4 w-4 text-yellow-600" />,
                    title: language === 'ko' ? '구조적으로 낮은 도로화물 마진' : 'Structurally Low Road Freight Margins',
                    desc: language === 'ko' ? '업계 전반의 낮은 마진으로 비용 충격 흡수 불가' : 'Industry-wide thin margins leave no buffer for cost shocks',
                  },
                  {
                    icon: <Building2 className="h-4 w-4 text-blue-600" />,
                    title: language === 'ko' ? '독일 산업 활동 부진' : 'Weak German Industrial Activity',
                    desc: language === 'ko' ? '독일 제조업 침체로 국내 화물 수요 감소' : 'German manufacturing slowdown reducing domestic freight demand',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 bg-white rounded p-3">
                    <span className="flex-shrink-0 mt-0.5">{item.icon}</span>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">{item.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 bg-white rounded p-3 text-sm text-gray-700 border-l-4 border-orange-400">
                <strong>{language === 'ko' ? 'Rainer Bisinger 대표: ' : 'MD Rainer Bisinger: '}</strong>
                {language === 'ko'
                  ? '"포괄적인 내부 최적화와 비용 절감 조치로도 이 막대한 부담을 더 이상 흡수할 수 없었습니다."'
                  : '"Comprehensive internal optimisation and cost-cutting measures were no longer able to absorb these massive burdens."'}
              </div>
            </div>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.8.section3.title')}
            </h2>
            <p className="mb-6">{t('article.8.p5')}</p>
            <p className="mb-6">{t('article.8.p6')}</p>

            {/* Section 4 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.8.section4.title')}
            </h2>
            <p className="mb-6">{t('article.8.p7')}</p>
            <p className="mb-6">{t('article.8.p8')}</p>

            {/* Austria Insolvency Wave Table */}
            <div className="bg-gray-50 p-6 rounded-lg my-8 border">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <Globe className="mr-2 h-5 w-5 text-gray-600" />
                {language === 'ko' ? 'EU 중견 운송업체 연쇄 파산 현황 (2026년 Q1–Q2)' : 'EU Mid-Market Transport Insolvency Wave (Q1–Q2 2026)'}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-100">
                      <th className="text-left py-2 px-3">{language === 'ko' ? '회사' : 'Company'}</th>
                      <th className="text-left py-2 px-3">{language === 'ko' ? '국가' : 'Country'}</th>
                      <th className="text-left py-2 px-3">{language === 'ko' ? '직원' : 'Employees'}</th>
                      <th className="text-left py-2 px-3">{language === 'ko' ? '부채 / 원인' : 'Liabilities / Trigger'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b bg-red-50">
                      <td className="py-2 px-3 font-semibold">Betz International</td>
                      <td className="py-2 px-3">🇩🇪 {language === 'ko' ? '독일' : 'Germany'}</td>
                      <td className="py-2 px-3 text-red-600 font-bold">140</td>
                      <td className="py-2 px-3 text-xs">{language === 'ko' ? '경유비·마진 압박·산업 침체' : 'Diesel costs, margin pressure, weak industry'}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3 font-semibold">Nothegger Transport Logistik</td>
                      <td className="py-2 px-3">🇦🇹 {language === 'ko' ? '오스트리아 (티롤)' : 'Austria (Tyrol)'}</td>
                      <td className="py-2 px-3 text-orange-600 font-bold">300</td>
                      <td className="py-2 px-3 text-xs">{language === 'ko' ? '€1,500만+ 부채, 팬데믹 이연 비용' : '€15M+ liabilities, deferred pandemic charges'}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3 font-semibold">Aksoy Transporte KG</td>
                      <td className="py-2 px-3">🇦🇹 {language === 'ko' ? '오스트리아 (티롤)' : 'Austria (Tyrol)'}</td>
                      <td className="py-2 px-3 text-yellow-600 font-bold">16</td>
                      <td className="py-2 px-3 text-xs">{language === 'ko' ? '사회보장 기여금 미납' : 'Outstanding social security contributions'}</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3 font-semibold">Renner Transport</td>
                      <td className="py-2 px-3">🇦🇹 {language === 'ko' ? '오스트리아 (부르겐란트)' : 'Austria (Burgenland)'}</td>
                      <td className="py-2 px-3 text-gray-600">—</td>
                      <td className="py-2 px-3 text-xs">{language === 'ko' ? '€210만 청구, 2024년 구조조정 계획 불이행' : '€2.1M claims, failed 2024 restructuring plan'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Structural Issues Box */}
            <div className="bg-yellow-50 p-6 rounded-lg my-8 border border-yellow-200">
              <h3 className="font-bold text-yellow-900 mb-3 flex items-center">
                <AlertTriangle className="mr-2 h-5 w-5" />
                {language === 'ko' ? 'EU 운송 시장 구조적 문제: 독일의 비용 열위' : 'EU Transport Market Structural Problem: German Cost Disadvantage'}
              </h3>
              <div className="text-sm text-gray-700 space-y-2">
                {[
                  language === 'ko' ? 'EU 회원국 간 에너지 가격·세금·정부 지원의 틀이 점점 벌어짐' : 'Framework conditions increasingly diverging across EU member states (energy, taxes, subsidies)',
                  language === 'ko' ? '독일: 평균 이상의 인건비 부담' : 'Germany: Above-average labour costs',
                  language === 'ko' ? '독일: 추가 규제 요건 (ETS2, CO2 통행료 등)' : 'Germany: Additional regulatory requirements (ETS2, CO2 tolling, etc.)',
                  language === 'ko' ? '국제 경쟁에서 이 비용들은 제한적으로만 상쇄 가능' : 'These costs can only be offset to a limited extent in international competition',
                  language === 'ko' ? '파산 상황에서는 더욱 불리하게 작용' : 'Especially disadvantageous in insolvency situations',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-yellow-600 font-bold flex-shrink-0">•</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Conclusion */}
            <div className="bg-gray-100 p-6 rounded-lg mt-8">
              <h3 className="font-bold text-gray-900 mb-3">
                {language === 'ko' ? '시사점: 중견 운송업체를 향한 경고 신호' : 'Outlook: A Warning Sign for Mid-Sized Transport Businesses'}
              </h3>
              <p className="text-sm text-gray-700">
                {language === 'ko'
                  ? 'Betz International의 파산은 몇 달째 나타나고 있는 추세를 상징합니다. 특히 중견·가족 기업들이 점점 더 압박을 받고 있는데, 단일 요인이 아니라 여러 구조적 부담의 상호작용 때문입니다. 경제 상황이 안정되지 않는다면 운송 부문의 통합은 계속될 가능성이 높습니다.'
                  : 'The insolvency of Betz International is emblematic of a trend that has been emerging for months. Mid-sized and family-owned companies in particular are increasingly coming under pressure — not due to a single factor, but because of the interplay of several structural burdens. If the economic situation does not stabilise, consolidation in the transport sector is likely to continue.'}
              </p>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} Trans.info / Tübingen Local Court — Betz International insolvency, 7 April 2026.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a
              href="https://trans.info/en/betz-insolvency-467974"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article8;
